import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AssistantApprovalStatus,
  AssistantMessageStatus,
  AssistantStreamEventType,
  AssistantToolCallStatus,
  AssistantToolName,
  type AssistantDecision,
  type AssistantMessagePart,
  type AssistantPageContext,
  type AssistantRunRequest,
  type AssistantStreamEvent,
  type AssistantUpload,
} from '@xtreed/academy-shared';
import i18n from '@/i18n';
import { notifyApiError } from '@/lib/api-message';
import {
  OPTIMISTIC_PREFIX,
  applyStreamEvent,
  markStreamingAs,
  optimisticUserMessage,
} from '@/lib/assistant-messages';
import {
  AssistantStreamError,
  streamAssistantRun,
} from '@/lib/assistant-stream';
import { assistantKeys, CONTENT_QUERY_KEYS } from './assistantKeys';
import { useUpdateMessagesCache } from './useAssistantMessages';

const READ_ONLY_TOOLS = new Set<string>([
  AssistantToolName.GET_MEMBER_AREA_OVERVIEW,
  AssistantToolName.GET_COURSE_TREE,
  AssistantToolName.GET_UPLOAD_STATUS,
  AssistantToolName.GET_MEMBER_AREA_LINK,
]);

export interface SendInput {
  conversationId: string;
  message: string;
  attachments: AssistantUpload[];
  context?: AssistantPageContext;
}

export function useAssistantRun() {
  const queryClient = useQueryClient();
  const updateMessages = useUpdateMessagesCache();
  const controllerRef = useRef<AbortController | null>(null);
  const [runningConversationId, setRunningConversationId] = useState<
    string | null
  >(null);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const execute = useCallback(
    async (conversationId: string, body: AssistantRunRequest) => {
      const controller = new AbortController();
      controllerRef.current = controller;
      setRunningConversationId(conversationId);
      const toolNames = new Map<string, string>();
      let started = false;

      const onEvent = (event: AssistantStreamEvent) => {
        if (event.type === AssistantStreamEventType.RUN_START) started = true;
        if (event.type === AssistantStreamEventType.TOOL_START) {
          toolNames.set(event.toolCallId, event.name);
        }
        updateMessages(conversationId, (messages) =>
          applyStreamEvent(messages, event),
        );
        if (
          event.type === AssistantStreamEventType.TOOL_END &&
          event.status === AssistantToolCallStatus.SUCCESS &&
          !READ_ONLY_TOOLS.has(toolNames.get(event.toolCallId) ?? '')
        ) {
          CONTENT_QUERY_KEYS.forEach((queryKey) =>
            queryClient.invalidateQueries({ queryKey: [...queryKey] }),
          );
        }
        if (event.type === AssistantStreamEventType.ERROR) {
          notifyApiError(
            { code: event.code },
            i18n.t('adminAssistant:errors.runFailed'),
          );
        }
      };

      try {
        await streamAssistantRun({
          conversationId,
          body,
          signal: controller.signal,
          onEvent,
        });
        return true;
      } catch (error) {
        if (controller.signal.aborted) {
          updateMessages(conversationId, (messages) =>
            markStreamingAs(messages, AssistantMessageStatus.ABORTED),
          );
          return true;
        }
        if (!started) {
          updateMessages(conversationId, (messages) =>
            messages.filter(
              (message) => !message.id.startsWith(OPTIMISTIC_PREFIX),
            ),
          );
        } else {
          updateMessages(conversationId, (messages) =>
            markStreamingAs(messages, AssistantMessageStatus.ERROR),
          );
        }
        notifyApiError(
          error instanceof AssistantStreamError ? { code: error.code } : error,
          i18n.t('adminAssistant:errors.runFailed'),
        );
        return false;
      } finally {
        controllerRef.current = null;
        setRunningConversationId(null);
        queryClient.invalidateQueries({
          queryKey: assistantKeys.conversations(),
        });
        queryClient.invalidateQueries({
          queryKey: assistantKeys.uploads(conversationId),
        });
      }
    },
    [queryClient, updateMessages],
  );

  const send = useCallback(
    ({ conversationId, message, attachments, context }: SendInput) => {
      const parts: AssistantMessagePart[] = [
        ...(message ? [{ type: 'text' as const, text: message }] : []),
        ...attachments.map((upload) => ({
          type: 'attachment' as const,
          uploadId: upload.id,
          filename: upload.filename,
          kind: upload.kind,
          mimeType: upload.mimeType,
          size: upload.size,
        })),
      ];
      updateMessages(conversationId, (messages) => [
        ...messages,
        optimisticUserMessage(conversationId, parts),
      ]);
      return execute(conversationId, {
        message,
        attachmentIds: attachments.map((upload) => upload.id),
        context,
      });
    },
    [execute, updateMessages],
  );

  const resume = useCallback(
    (
      conversationId: string,
      interruptId: string,
      decisions: AssistantDecision[],
    ) => {
      updateMessages(conversationId, (messages) =>
        messages.map((message) => ({
          ...message,
          parts: message.parts.map((part) =>
            part.type === 'approval' && part.interruptId === interruptId
              ? { ...part, status: AssistantApprovalStatus.RESOLVED, decisions }
              : part,
          ),
        })),
      );
      return execute(conversationId, { resume: { interruptId, decisions } });
    },
    [execute, updateMessages],
  );

  const stop = useCallback(() => controllerRef.current?.abort(), []);

  return {
    send,
    resume,
    stop,
    isRunning: runningConversationId !== null,
    runningConversationId,
  };
}
